import { useState } from 'react';
import { motion } from 'framer-motion';
import { Answer } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Check, X, HelpCircle } from 'lucide-react';

interface BooleanInputProps {
  answer: Answer;
  onBlur: (value: { booleanValue: boolean }) => void;
  disabled?: boolean;
}

export function BooleanInput({ answer, onBlur, disabled }: BooleanInputProps) {
  const [value, setValue] = useState<boolean | null>(answer.booleanValue);
  const [isHovered, setIsHovered] = useState(false);

  const handleSelect = (selectedValue: boolean) => {
    if (disabled) return;
    
    setValue(selectedValue);
    if (selectedValue !== answer.booleanValue) {
      onBlur({ booleanValue: selectedValue });
    }
  };

  return (
    <div
      style={{
        padding: spacing[4],
        backgroundColor: colors.white,
        borderRadius: 16,
        border: `1px solid ${colors.gray[200]}`,
        transition: 'all 0.2s ease',
        ...(isHovered && {
          borderColor: colors.primary[300],
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.1)',
        }),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[3],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
              marginBottom: spacing[1],
            }}
          >
            <HelpCircle size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600] }}>
              {answer.question.description}
            </p>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: spacing[3],
          marginTop: spacing[2],
        }}
      >
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSelect(true)}
          disabled={disabled}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing[2],
            padding: spacing[3],
            borderRadius: 12,
            border: `2px solid ${
              value === true
                ? colors.success?.[500] || '#10b981'
                : colors.gray[200]
            }`,
            backgroundColor: value === true
              ? colors.success?.[50] || '#ecfdf5'
              : colors.white,
            color: value === true
            // @ts-ignore
              ? colors.success?.[700] || '#047857'
              : colors.gray[700],
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <Check size={18} />
          <span style={{ fontWeight: 600 }}>Sí</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSelect(false)}
          disabled={disabled}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing[2],
            padding: spacing[3],
            borderRadius: 12,
            border: `2px solid ${
              value === false
                ? colors.error?.[500] || '#ef4444'
                : colors.gray[200]
            }`,
            backgroundColor: value === false
              ? colors.error?.[50] || '#fef2f2'
              : colors.white,
            color: value === false
            // @ts-ignore
              ? colors.error?.[700] || '#b91c1c'
              : colors.gray[700],
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <X size={18} />
          <span style={{ fontWeight: 600 }}>No</span>
        </motion.button>
      </div>
    </div>
  );
}